// DeckEditorRoute: Route-level container for a deck editor view.
// Fetches deck data by route param and renders a minimal editor page for now.
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useNavigate } from 'react-router-dom';
import { json } from '../../api/apiClient';
import DeckEditorPage from './DeckEditorPage';
import MobileDeckEditorPage from './mobile/MobileDeckEditorPage';
import { useIsMobileViewport } from '../../ui/breakpoints';
import ProfileBanner from '../../components/ProfileBanner';
import Page from '../../ui/Page';
import Skeleton from '../../ui/Skeleton';
import EmptyState from '../../ui/EmptyState';
import { RefreshCw, SearchX } from 'lucide-react';
import { useRefetchOnWindowFocus } from './hooks/useRefetchOnWindowFocus';
import { useDeckSyncChannel } from './hooks/useDeckSyncChannel';
import { deckNumericId } from './utils/deckIds';

export default function DeckEditorRoute() {
  const { deckId } = useParams();
  const { authToken, currentUser, logout } = useAuth();
  const { viewedUserId, isOwnProfile } = useProfile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobileEditor = useIsMobileViewport();

  const queryKey = useMemo(() => ['deck-editor', deckId, viewedUserId], [deckId, viewedUserId]);

  const deckQuery = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        const viewerParam = currentUser?.id ? `viewerUserId=${encodeURIComponent(currentUser.id)}` : '';
        const ownerParam = viewedUserId ? `userId=${encodeURIComponent(viewedUserId)}` : '';
        const editorParam = 'editor=true'; // Für Manakosten und Editor-Einstellungen
        const qs = [ownerParam, viewerParam, editorParam].filter(Boolean).join('&');
        const url = qs ? `/api/decks/${deckId}?${qs}` : `/api/decks/${deckId}`;
        return await json(url, { signal });
      } catch (err) {
        if (err && err.status === 401) {
          logout();
          navigate('/auth', { replace: true });
        }
        throw err;
      }
    },
    enabled: !!deckId && !!authToken,
    staleTime: 1000 * 60 * 5,
  });

  // Handler für lokale Deck-Updates (optimistisch, merge-safe)
  // Unterstützt entweder:
  //   - onDeckUpdate(nextDeck)
  //   - onDeckUpdate(prev => nextDeck)
  const handleDeckUpdate = useCallback((update) => {
    queryClient.setQueryData(queryKey, (prev) => {
      if (typeof update === 'function') {
        return update(prev);
      }
      return update;
    });
  }, [queryClient, queryKey]);

  const numericDeckId = useMemo(() => deckNumericId(deckQuery.data), [deckQuery.data]);
  useRefetchOnWindowFocus(numericDeckId);
  useDeckSyncChannel({ uiDeckId: deckId, numericDeckId });

  if (deckQuery.isLoading) {
    return (
      <Page width="full">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} aria-busy="true">
          <Skeleton width="320px" height="34px" />
          <Skeleton height="220px" radius="lg" />
          <Skeleton height="41px" radius="lg" />
          <Skeleton height="55vh" radius="lg" />
        </div>
      </Page>
    );
  }
  if (deckQuery.error) {
    return (
      <Page width="full">
        <EmptyState
          variant="error"
          icon={RefreshCw}
          title="Deck konnte nicht geladen werden"
          description="Prüfe deine Verbindung und versuche es erneut."
          onRetry={() => deckQuery.refetch()}
        />
      </Page>
    );
  }
  if (!deckQuery.data) {
    return (
      <Page width="full">
        <EmptyState icon={SearchX} title="Deck nicht gefunden" description="Dieses Deck existiert nicht oder wurde gelöscht." />
      </Page>
    );
  }

  return (
    <>
      <ProfileBanner />
      {isMobileEditor ? (
        <MobileDeckEditorPage
          selectedDeck={deckQuery.data}
          isOwnProfile={isOwnProfile}
          onDeckUpdate={handleDeckUpdate}
        />
      ) : (
        <DeckEditorPage
          selectedDeck={deckQuery.data}
          isOwnProfile={isOwnProfile}
          onDeckUpdate={handleDeckUpdate}
        />
      )}
    </>
  );
}
